import type { AppDispatch, GetState } from '../store';
import type { ApiAccountJSON } from '../api_types/accounts';
import type { EncryptedPayload, MultiAccountEntry } from '../types/multi_account';
import {
  MultiAccountSwitchError,
  SwitchErrorCode,
} from '../types/multi_account';
import api, { setActiveAccountToken, updateCSRFToken } from '../api';
import {
  deleteEncryptedToken,
  loadEncryptedToken,
  saveEncryptedToken,
} from '../utils/multi_account_db';
import { decryptToken, encryptToken } from '../utils/multi_account_crypto';
import { MULTI_ACCOUNT_REQUEST_TIMEOUT } from '../api/multi_accounts_constants';
import { importFetchedAccount } from './importer';
import SwitchLogger from '../utils/switch_logger';
import {
  clearActiveAccountIdIfMatches,
  markSwitchIntent,
  setActiveAccountIdInStorage,
} from '../utils/multi_account_storage';

const refreshOAuthToken = async (
  dispatch: AppDispatch,
  entry: MultiAccountEntry,
): Promise<string> => {
  const {
    fetchAuthorizeEntry,
    consumeAuthorizationCode,
    restoreMultiAccountSession,
  } = await import('../api/multi_accounts');
  const { openOAuthPopup } = await import(
    '../features/multi_account/callback_handler'
  );

  const pending = { state: null as string | null, nonce: null as string | null };

  try {
    const authorizeEntry = await fetchAuthorizeEntry({ forceLogin: false });
    pending.state = authorizeEntry.state;
    pending.nonce = authorizeEntry.nonce;

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const blankPopup = window.open(
      'about:blank',
      'multi-account-oauth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no`,
    );

    if (!blankPopup) {
      throw new Error('팝업이 차단되었습니다. 브라우저에서 팝업을 허용해주세요.');
    }

    const { authorize_url, state, nonce } = authorizeEntry;
    const callback = await openOAuthPopup(authorize_url, state, blankPopup);

    const { token, account } = await consumeAuthorizationCode({
      state: callback.state,
      nonce,
      authorization_code: callback.code,
    });

    const refreshedEntry: MultiAccountEntry = {
      ...entry,
      id: account.id ?? entry.id,
      acct: account.acct ?? entry.acct,
      displayName:
        account.display_name ?? account.username ?? entry.displayName,
      avatar: account.avatar ?? account.avatar_static ?? entry.avatar,
      encryptedTokenRef: entry.encryptedTokenRef,
      lastUsedAt: new Date().toISOString(),
    };

    await dispatch(registerAccount(refreshedEntry, token) as unknown as any);
    return token;
  } catch (error) {
    if (pending.state && pending.nonce) {
      try {
        await restoreMultiAccountSession({
          state: pending.state,
          nonce: pending.nonce,
        });
      } catch (restoreError) {
        console.error(
          'Failed to restore multi-account session after token refresh failure:',
          restoreError,
        );
      }
    }

    throw error;
  }
};

type CsrfInfo = {
  csrfToken: string;
  formToken?: string;
};

const readInitialCsrfInfo = (): CsrfInfo | null => {
  const csrfToken = document
    .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
    ?.getAttribute('content')
    ?.trim();

  if (!csrfToken) {
    return null;
  }

  return {
    csrfToken,
    formToken: undefined,
  };
};

let cachedCsrfInfo: CsrfInfo | null = readInitialCsrfInfo();

const setCachedCsrfInfo = (info: CsrfInfo) => {
  cachedCsrfInfo = info;
  updateCSRFToken(info.csrfToken);
};

// Action types
export const MULTI_ACCOUNT_HYDRATE = 'MULTI_ACCOUNT_HYDRATE';
export const MULTI_ACCOUNT_REGISTER = 'MULTI_ACCOUNT_REGISTER';
export const MULTI_ACCOUNT_SWITCH = 'MULTI_ACCOUNT_SWITCH';
export const MULTI_ACCOUNT_REMOVE = 'MULTI_ACCOUNT_REMOVE';
export const MULTI_ACCOUNT_TOUCH = 'MULTI_ACCOUNT_TOUCH';

// Action creators
export const hydrateMultiAccountAction = (payload: {
  activeAccountId: string | null;
  accounts: Record<string, MultiAccountEntry>;
}) => ({
  type: MULTI_ACCOUNT_HYDRATE,
  payload,
});

export const registerAccountAction = (payload: MultiAccountEntry) => ({
  type: MULTI_ACCOUNT_REGISTER,
  payload,
});

export const switchAccountAction = (accountId: string) => ({
  type: MULTI_ACCOUNT_SWITCH,
  payload: { accountId },
});

export const removeAccountAction = (accountId: string) => ({
  type: MULTI_ACCOUNT_REMOVE,
  payload: { accountId },
});

export const touchAccountAction = (accountId: string) => ({
  type: MULTI_ACCOUNT_TOUCH,
  payload: { accountId },
});

// Thunks
export const registerAccount =
  (entry: MultiAccountEntry, token: string) =>
  async (dispatch: AppDispatch) => {
    try {
      const lastUsedAt = new Date().toISOString();
      const entryWithTimestamp = {
        ...entry,
        lastUsedAt,
      };

      // Encrypt and save the token
      const encryptedPayload = await encryptToken(token);
      await saveEncryptedToken(entry.id, encryptedPayload, entryWithTimestamp);

      // Register the account in Redux
      dispatch(registerAccountAction(entryWithTimestamp));
    } catch (error) {
      console.error('Failed to register account:', error);
      throw error;
    }
  };

export const switchAccount =
  (accountId: string) => async (dispatch: AppDispatch, getState: GetState) => {
    const startTime = SwitchLogger.logSwitchAttempt(accountId);
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark(`multi_account_switch_start_${accountId}`);
    }

    // 서버 세션이 실제로 바뀐 뒤인지. 바뀐 뒤의 실패는 조용히 끝낼 수 없다.
    let sessionSwitched = false;

    const reloadToMatchSession = () => {
      if (
        typeof window !== 'undefined' &&
        typeof window.location !== 'undefined'
      ) {
        window.location.reload();
      }
    };

    try {
      const state: any = getState();
      const hasGetIn = typeof state?.getIn === 'function';
      const accountsSource = hasGetIn
        ? state.getIn(['multiAccount', 'accounts'])
        : state?.multiAccount?.accounts ?? null;
      const accountExists =
        !!accountsSource &&
        (typeof accountsSource?.has === 'function'
          ? accountsSource.has(accountId)
          : Object.prototype.hasOwnProperty.call(accountsSource, accountId));

      if (!accountExists) {
        throw new MultiAccountSwitchError(
          SwitchErrorCode.ACCOUNT_NOT_FOUND,
          `Account ${accountId} not found`,
        );
      }

      const { refreshSession } = await import('../api/multi_accounts');

      const encryptedPayload = await loadEncryptedToken(accountId);
      if (!encryptedPayload) {
        throw new MultiAccountSwitchError(
          SwitchErrorCode.TOKEN_MISSING,
          '저장된 토큰을 찾을 수 없습니다. 계정을 다시 추가해주세요.',
        );
      }

      let refreshToken: string;
      try {
        refreshToken = await decryptToken(encryptedPayload);
      } catch (decryptError) {
        // NOTE: 절대로 여기서 resetCryptoKey()를 호출하면 안 된다.
        // 마스터 키를 삭제하면 이 계정뿐 아니라 저장된 '모든' 계정의 토큰이
        // 영구적으로 복호화 불능이 되며, 재시도는 새 키로 옛 암호문을 풀 수 없어 무의미하다.
        // 대신 이 계정의 토큰만 죽은 것으로 간주하고 구조화된 에러로 표면화한다.
        console.error(
          `Failed to decrypt refresh token for account ${accountId}:`,
          decryptError,
        );
        SwitchLogger.logSwitchFailure(
          accountId,
          'failed_to_decrypt_refresh_token',
          startTime,
        );

        throw new MultiAccountSwitchError(
          SwitchErrorCode.TOKEN_INVALID,
          '저장된 계정 토큰을 복호화할 수 없습니다. 계정을 다시 추가해주세요.',
        );
      }

      try {
        document.cookie.split(';').forEach((cookie) => {
          const trimmed = cookie.replace(/^ +/, '');
          const eqPos = trimmed.indexOf('=');
          const name = eqPos > -1 ? trimmed.substring(0, eqPos) : trimmed;
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
        });
      } catch (cookieError) {
        console.warn('[Switch] Failed to clear cookies:', cookieError);
      }

      const entryRecord =
        typeof accountsSource?.get === 'function'
          ? accountsSource.get(accountId)
          : accountsSource?.[accountId];
      const entry: MultiAccountEntry | null = entryRecord
        ? entryRecord.toJS
          ? entryRecord.toJS()
          : entryRecord
        : null;

      let currentEncryptedPayload: EncryptedPayload | null = encryptedPayload;

      const refreshResponse = await (async () => {
        try {
          const response = await refreshSession(refreshToken);
          const csrfHeader =
            response.headers?.['x-csrf-token'] ??
            (response.headers?.['X-CSRF-Token'] as string | undefined);
          if (typeof csrfHeader === 'string' && csrfHeader.trim().length > 0) {
            setCachedCsrfInfo({
              csrfToken: csrfHeader.trim(),
              formToken: undefined,
            });
          }
          return response;
        } catch (refreshError) {
        const status = (refreshError as any)?.response?.status;
        console.warn(
          `Failed to refresh session for account ${accountId}:`,
          refreshError,
        );

        if (status && [400, 401, 403, 422].includes(status)) {
          throw new MultiAccountSwitchError(
            SwitchErrorCode.TOKEN_INVALID,
            '저장된 계정 토큰이 만료되었거나 사용할 수 없습니다. 계정을 다시 로그인하여 추가해주세요.',
          );
        }

        throw refreshError;
      }
      })();

      // @_longwhile — **여기서부터 서버 세션은 이미 바뀌었다** (`refresh` 가
      //   `reset_session` + `sign_in` 을 했다). 뒤에서 무슨 일이 나든 이
      //   페이지는 남의 세션을 물고 있는 셈이라, 에러만 던지고 멈추면
      //   화면(옛 계정)과 세션(새 계정)이 갈라진 채로 남는다 — CSRF 토큰도
      //   이미 무효다. 응답에 토큰이 빠져 있어도 마찬가지다.
      sessionSwitched = true;

      const sessionToken = refreshResponse?.data?.token as string | undefined;

      if (!sessionToken) {
        throw new MultiAccountSwitchError(
          SwitchErrorCode.SESSION_TOKEN_MISSING,
          '세션 토큰을 발급받지 못했습니다.',
        );
      }

      setActiveAccountToken(sessionToken);

      // @_longwhile — 저장된 토큰의 **실제 주인**이 기준이다.
      //   키(accountId)와 토큰 주인이 어긋난 기록이 남아 있을 수 있는데,
      //   그대로 두면 이 항목을 누를 때마다 남의 세션을 물고 온다. 아래에서
      //   어긋난 키는 지우고 확인된 id 로 다시 저장한다.
      const updateEntryMetadata = async (
        accountData: ApiAccountJSON | null,
      ): Promise<void> => {
        if (!accountData) {
          return;
        }

        const resolvedId = accountData.id ?? accountId;
        const normalizedEntry: MultiAccountEntry = {
          id: resolvedId,
          acct:
            accountData.acct ??
            accountData.username ??
            entry?.acct ??
            resolvedId,
          displayName:
            accountData.display_name ??
            accountData.username ??
            entry?.displayName ??
            '',
          avatar:
            accountData.avatar ??
            accountData.avatar_static ??
            entry?.avatar ??
            '',
          encryptedTokenRef: entry?.encryptedTokenRef ?? '',
          lastUsedAt: new Date().toISOString(),
        };

        if (resolvedId !== accountId) {
          console.error(
            `[MultiAccount] Stored token for ${accountId} belongs to ${resolvedId}; repairing the stored entry.`,
          );

          try {
            await deleteEncryptedToken(accountId);
          } catch (deleteError) {
            console.error(
              `Failed to drop the mismatched entry for account ${accountId}:`,
              deleteError,
            );
          }

          dispatch(removeAccountAction(accountId));
          clearActiveAccountIdIfMatches(accountId);
        }

        dispatch(registerAccountAction(normalizedEntry));

        if (currentEncryptedPayload) {
          try {
            await saveEncryptedToken(
              resolvedId,
              currentEncryptedPayload,
              normalizedEntry,
            );
          } catch (saveError) {
            console.error(
              `Failed to update stored metadata for account ${resolvedId}:`,
              saveError,
            );
          }
        }
      };

      const verifiedAccount = await (async () => {
        try {
          const response = await api().get('/api/v1/accounts/verify_credentials', {
            timeout: MULTI_ACCOUNT_REQUEST_TIMEOUT,
          });
          dispatch(importFetchedAccount(response.data));
          return response.data as ApiAccountJSON;
        } catch (verifyError) {
        const status = (verifyError as any)?.response?.status;
        const errorDescription =
          (verifyError as any)?.response?.data?.error_description ?? '';

        const isInvalidToken =
          status === 401 ||
          (status === 403 &&
            typeof errorDescription === 'string' &&
            errorDescription.toLowerCase().includes('invalid_token'));

        if (isInvalidToken) {
          throw new Error(
            '저장된 계정 토큰이 만료되었거나 사용할 수 없습니다. 계정을 다시 로그인하여 추가해주세요.',
          );
        }

        console.error(
          `Failed to verify credentials for account ${accountId}:`,
          verifyError,
        );
        throw verifyError;
      }
      })();

      await updateEntryMetadata(verifiedAccount);

      // 세션은 이미 서버에서 바뀌었다. 포인터는 **실제로 로그인된 계정**을
      // 가리켜야 한다 — 요청한 계정이 아니라.
      const activatedAccountId = verifiedAccount?.id ?? accountId;

      setActiveAccountIdInStorage(activatedAccountId);
      // @_longwhile — 표식은 **사용자가 누른 계정**으로 남긴다. 새로고침한 뒤
      //   세션이 그 계정이 아니면(쿠키가 안 붙었거나, 저장된 토큰의 주인이
      //   달라 엉뚱한 계정으로 들어갔거나) `main.tsx` 가 알려 준다. 예전에는
      //   화면만 그대로 돌아오고 아무 말이 없어서 "눌러도 아무 일도 일어나지
      //   않는" 것으로 보였다.
      markSwitchIntent(accountId);
      SwitchLogger.logSwitchSuccess(activatedAccountId, startTime);

      reloadToMatchSession();

      return sessionToken;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      SwitchLogger.logSwitchFailure(accountId, errorMessage, startTime);
      console.error('Failed to switch account:', error);

      if (sessionSwitched) {
        // 세션이 이미 바뀐 뒤의 실패다. 사용자가 누른 계정을 표식으로 남기고
        // 새로고침하면, 어디로 들어갔든 화면이 세션과 맞고 다른 계정으로
        // 들어갔을 때는 `main.tsx` 가 알려 준다.
        markSwitchIntent(accountId);
        reloadToMatchSession();
        return null;
      }

      throw error;
    }
  };

export const removeAccount =
  (accountId: string) => async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const state: any = getState();
      const wasActive =
        state?.getIn?.(['multiAccount', 'activeAccountId']) === accountId;

      // Delete the encrypted token
      await deleteEncryptedToken(accountId);

      // Remove from Redux state
      dispatch(removeAccountAction(accountId));

      if (wasActive) {
        setActiveAccountToken(null);
      }

      clearActiveAccountIdIfMatches(accountId);
    } catch (error) {
      console.error('Failed to remove account:', error);
      throw error;
    }
  };
